"use client";

import { cn } from "@/lib/utils";
import { CheckIcon } from "../CheckIcon";
import { DidYouKnowDisclosure } from "../DidYouKnowDisclosure";
import { FeatureList } from "../FeatureList";
import { FounderTipCard } from "../FounderTipCard";
import { HkAccountantNote } from "../HkAccountantNote";
import { MonthlyExpensesSelect } from "../MonthlyExpensesSelect";
import { OrderSummaryCard } from "../OrderSummaryCard";
import { PlanCard } from "../PlanCard";
import { AUDIT_PLANS, type PackageSummary } from "../pricing";

const BENEFITS = [
  "Audited financial statements prepared under HK standards",
  "Profits Tax computation and filing included",
  "Smooth, guided process with expert CPA support",
];

// Same two lists as the Company Secretary screen — the left column is the
// PayPal/Airwallex/Stripe set again, which reads as placeholder content on an
// audit & tax step.
const INCLUDED = [
  "HK Company Registration",
  "PayPal with VIP Support (6% dispute tolerance)",
  "Payoneer VIP Account and 5+ more banks",
  "Airwallex Account",
  
  "Access to 9+ PayPal Reps",
  "Unlimited PayPal Reactivations",
  "Stripe",
];

const INCLUDED_ADVANCED = [
  "Change of company name (government fee excluded)",
  "Allotment of Shares",
  "Transfer of Shares",
  "Change of director (appointment/cessation)",
];

/**
 * Shared by both branches' audit & tax screens, which are the same screen with
 * different copy: the manage branch has a longer intro, swaps the founder tip
 * for a "Not sure which plan applies?" note, and words its skip link
 * differently. Everything else — benefits, tiers, feature lists — is identical,
 * so it lives here once.
 */
export function AuditTaxStep({
  monthlyExpenses,
  onMonthlyExpensesChange,
  auditPlanId,
  onAuditPlanChange,
  summary,
  onNext,
  onSkip,
  intro = "Hong Kong companies must prepare annual audits and tax filings.",
  note,
  skipLabel = "Skip this",
  compactNext = false,
}: {
  monthlyExpenses: string;
  onMonthlyExpensesChange: (value: string) => void;
  auditPlanId: string | null;
  onAuditPlanChange: (id: string) => void;
  summary: PackageSummary;
  onNext: () => void;
  onSkip: () => void;
  intro?: React.ReactNode;
  /** Sits where the founder tip does; defaults to that tip. */
  note?: React.ReactNode;
  skipLabel?: string;
  /** The manage design's 120px Next, against the register branch's 188px. */
  compactNext?: boolean;
}) {
  // Read from the plan list rather than hardcoding the label, so renaming a
  // tier doesn't leave the upsell copy pointing at a plan that no longer exists.
  const premiumName = AUDIT_PLANS.find((plan) => plan.id === "premium")?.name ?? "Premium";
  const advancedIncluded = auditPlanId === "premium";
  const advancedLocked = auditPlanId === "standard";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (auditPlanId) onNext();
      }}
      className="mx-auto w-full max-w-[1250px] px-4 pb-16 pt-8 sm:px-6 sm:pb-24 sm:pt-12 lg:pt-[31px]"
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        {/* Left column */}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-6 sm:gap-8">
          <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
            <h1 className="font-suisse-intl-bold text-[28px] leading-tight text-white sm:text-4xl md:text-5xl lg:text-[54px] lg:leading-[71px]">
              Stay compliant with
              <br />
              annual tax filing
            </h1>
            <p className="font-suisse-intl-medium text-base leading-relaxed text-white sm:text-lg sm:leading-[28.8px]">
              {intro}
            </p>
          </div>

          {/* items-start, not items-center: these lines wrap on a phone, and
              centring parks the tick halfway down a two-line block. */}
          <ul className="flex w-full flex-col items-start gap-3 sm:gap-2">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex w-full items-start gap-[10px]">
                <CheckIcon className="relative mt-0.5 block h-6 w-6 shrink-0" />
                <span className="flex-1 font-inter text-sm font-bold leading-[19.6px] text-[#F3F4F6]">
                  {benefit}
                </span>
              </li>
            ))}
          </ul>

          <HkAccountantNote />

          <hr className="w-full border-t border-[#283143]" />

          <div className="flex w-full flex-col items-start gap-[10px]">
            <h2 className="font-inter text-base font-bold text-[#F3F4F6] sm:text-lg">
              Choose Your Plan – Get the Right Fit for Your Business
            </h2>
            <p className="font-inter text-sm text-[#9CA3AF]">
              Your plan is based on your monthly expenses. If you&apos;re unsure, start with the
              lowest tier — you can adjust later.
            </p>
          </div>

          <div className="flex w-full flex-col items-start gap-2">
            <MonthlyExpensesSelect value={monthlyExpenses} onChange={onMonthlyExpensesChange} />
            <DidYouKnowDisclosure />
          </div>

          <div className="flex w-full flex-col items-center gap-6 sm:gap-8">
            {/* Side by side only from lg, matching the Company Secretary tiers:
                two cards at 640px leave too little room for the price pair. */}
            <fieldset className="flex w-full flex-col gap-6 sm:gap-8 lg:flex-row lg:items-stretch">
              <legend className="sr-only">Choose an audit &amp; tax plan</legend>
              {AUDIT_PLANS.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  selected={auditPlanId === plan.id}
                  onSelect={onAuditPlanChange}
                />
              ))}
            </fieldset>

            {/* The advanced list is premium-only, so it reframes itself against
                the current choice: an upsell on Standard, confirmation on
                Premium, and neutral until something is picked — claiming an
                upgrade is needed before the customer has chosen would be
                presumptuous. */}
            <div className="flex w-full flex-col gap-6 sm:flex-row">
              <FeatureList title="What's included" items={INCLUDED} />
              <FeatureList
                title={
                  advancedLocked ? `Upgrade to ${premiumName} for these` : "Included (Advanced)"
                }
                items={INCLUDED_ADVANCED}
                tone={advancedIncluded ? "included" : advancedLocked ? "locked" : "default"}
              />
            </div>
          </div>

          {note ?? (
            <FounderTipCard>
              The IRD only recognises expenses that are properly recorded. Clean books from day one
              mean you only pay tax on what you actually owe — nothing more.
            </FounderTipCard>
          )}

          {/* col-reverse on phones so the primary action sits above "Skip
              this" despite coming second in the DOM. */}
          <div className="flex w-full flex-col-reverse items-stretch gap-3 pt-4 sm:flex-row sm:items-center sm:justify-end sm:gap-4 sm:pt-6">
            <button
              type="button"
              onClick={onSkip}
              className="px-4 py-3 font-inter text-sm font-bold text-white transition-opacity hover:opacity-80"
            >
              {skipLabel}
            </button>
            <button
              type="submit"
              disabled={!auditPlanId}
              className={cn(
                "flex h-14 w-full items-center justify-center rounded-full bg-white px-4 font-suisse-intl text-xl leading-5 text-[#2A4775] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50",
                compactNext ? "sm:w-[120px]" : "sm:w-[188px]",
              )}
            >
              Next
            </button>
          </div>
        </div>

        {/* Right column — order summary */}
        <OrderSummaryCard summary={summary} className="lg:w-[402px] lg:shrink-0" />
      </div>
    </form>
  );
}
